import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from "react";

type RevealDirection = "up" | "down" | "left" | "right" | "none";

interface RevealProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  direction?: RevealDirection;
  delay?: number;
  duration?: number;
  distance?: number;
  repeat?: boolean;
}

function offsetFor(direction: RevealDirection, distance: number): string {
  switch (direction) {
    case "up":
      return `translate3d(0, ${distance}px, 0)`;
    case "down":
      return `translate3d(0, -${distance}px, 0)`;
    case "left":
      return `translate3d(${distance}px, 0, 0)`;
    case "right":
      return `translate3d(-${distance}px, 0, 0)`;
    default:
      return "none";
  }
}

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

// SSR-safe layout effect
const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Scroll-triggered reveal.
 *
 * SSR-safe: defaults to VISIBLE so server HTML always shows content. On the
 * client, off-screen elements are hidden then revealed on scroll via
 * IntersectionObserver. If JS fails to hydrate or IO is unavailable, content
 * stays visible — no blank pages.
 */
export function Reveal({
  children,
  direction = "up",
  delay = 0,
  duration = 600,
  distance = 24,
  repeat = false,
  style,
  ...rest
}: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(true);

  useIsoLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const rect = node.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const inView = rect.top < vh && rect.bottom > 0;
    if (inView && !repeat) return; // already visible, no need to animate
    setVisible(false);
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            if (!repeat) io.disconnect();
          } else if (repeat) {
            setVisible(false);
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -5% 0px" },
    );
    io.observe(node);
    const fallback = window.setTimeout(() => setVisible(true), 1500);
    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
    };
  }, [repeat]);

  const computedStyle: CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? "translate3d(0,0,0)" : offsetFor(direction, distance),
    transition: `opacity ${duration}ms ${EASE} ${delay}ms, transform ${duration}ms ${EASE} ${delay}ms`,
    willChange: "opacity, transform",
    ...style,
  };

  return (
    <div ref={ref} style={computedStyle} {...rest}>
      {children}
    </div>
  );
}

interface RevealStaggerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  stagger?: number;
  delay?: number;
  repeat?: boolean;
}

interface StaggerContext {
  visible: boolean;
  stagger: number;
  delay: number;
}

export function RevealStagger({
  children,
  stagger = 80,
  delay = 0,
  repeat = false,
  style,
  ...rest
}: RevealStaggerProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(true);

  useIsoLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const rect = node.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const inView = rect.top < vh && rect.bottom > 0;
    if (inView && !repeat) return;
    setVisible(false);
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            if (!repeat) io.disconnect();
          } else if (repeat) {
            setVisible(false);
          }
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -5% 0px" },
    );
    io.observe(node);
    const fallback = window.setTimeout(() => setVisible(true), 1500);
    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
    };
  }, [repeat]);

  return (
    <div
      ref={ref}
      data-reveal-stagger=""
      data-reveal-visible={visible ? "true" : "false"}
      style={{
        ...style,
      }}
      {...rest}
    >
      <StaggerProvider value={{ visible, stagger, delay }}>{children}</StaggerProvider>
    </div>
  );
}

const StaggerCtx = createContext<StaggerContext | null>(null);

function StaggerProvider({ value, children }: { value: StaggerContext; children: ReactNode }) {
  const wrapped = Children.map(children, (child, index) => {
    if (!isValidElement(child)) return child;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return cloneElement(child as React.ReactElement<any>, { __staggerIndex: index });
  });
  return <StaggerCtx.Provider value={value}>{wrapped}</StaggerCtx.Provider>;
}

interface RevealItemProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  distance?: number;
  __staggerIndex?: number;
}

export function RevealItem({
  children,
  distance = 20,
  style,
  __staggerIndex = 0,
  ...rest
}: RevealItemProps) {
  const ctx = useContext(StaggerCtx);
  const visible = ctx?.visible ?? true;
  const stagger = ctx?.stagger ?? 0;
  const baseDelay = ctx?.delay ?? 0;
  const itemDelay = baseDelay + __staggerIndex * stagger;

  const computedStyle: CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? "translate3d(0,0,0)" : `translate3d(0, ${distance}px, 0)`,
    transition: `opacity 550ms ${EASE} ${itemDelay}ms, transform 550ms ${EASE} ${itemDelay}ms`,
    willChange: "opacity, transform",
    ...style,
  };

  return (
    <div style={computedStyle} {...rest}>
      {children}
    </div>
  );
}
